import { ApolloCache, ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  IDeleteDeductionBackupDocumentParams,
  IDeleteDeductionBackupDocumentRepositoryResponse,
  IDownloadRemittanceDocumentsParams,
  IDownloadRemittanceDocumentsRepositoryResponse,
  IGetIncompleteDeductionResolutionStatusParams,
  IGetRemittanceItemByIdParams,
  IGetRemittanceItemByIdRepositoryResponse,
  IGetRemittanceItemCountParams,
  IGetRemittanceParams,
  IGetRemittanceWithIncompleteDeductionsParams,
  IIncompleteDeductionResolutionStatusRepositoryResponse,
  IRemittanceItemCountRepositoryResponse,
  IRemittanceItemRepositoryResponse,
  IRemittanceItemSummary,
  IRemittanceRepository,
  IRemittanceRepositoryResponse,
} from "@/core/repositories/remittance";
import { Remittance, RemittanceItem, RemittanceItemProps, RemittanceProps, IncompleteDeductionResolutionStatus } from "@/core/entities/deduction/remittance";
import {
  ADD_REMITTANCE,
  GET_REMITTANCE_ITEM,
  GET_REMITTANCES,
  GET_REMITTANCE_ITEM_COUNT,
  GET_REMITTANCE_ITEM_WITH_INCOMPLETE_DEDUCTIONS,
  GET_INCOMPLETE_DEDUCTION_RESOLUTION_STATUS,
  DELETE_DEDUCTION_BACKUP_DOCUMENT,
} from "@/core/graphql/remittance";
import { ApiEndpoints, AppConfig } from "@/infrastructure/services/config";
import { AddRemittanceDTO } from "@/core/dto/deduction/remittance/addRemittance";
import { IAddRemittanceProcessedResult } from "./RemittanceAdd.interface";
import { DeductionCount } from "@/core/entities/deduction";
import GET_REMITTANCES_WITH_INCOMPLETE_DEDUCTIONS from "@/core/graphql/remittance/getRemittanceWithIncompleteDeductions";
import { GET_REMITTANCE_ITEM_BY_ID } from "@/core/graphql/remittance/getRemittanceItemById";
import { ContentType } from "@ir/core/http";
import { PrivateHttp } from "@/infrastructure/adapters/http/PrivateHttp.adapter";
import { IColumnAlias } from "@/core/repositories/deduction";

const _appConfig = AppConfig.getInstance();

const TIME_ZONE = _appConfig.TIMEZONE;

interface IQueryResponse<T> {
  total: number;
  page: number;
  limit: number;
  items: T[];
}

interface IRemittanceQueryResponse {
  remittances: IQueryResponse<RemittanceProps> & { columns: IColumnAlias[] };
}
interface IRemittanceWithIncompleteDeductionsQueryResponse {
  remittancesWithIncompleteDeductions: IQueryResponse<RemittanceProps> & { columns: IColumnAlias[] };
}

interface IRemittanceItemQueryResponse {
  remittanceItems: IQueryResponse<RemittanceItemProps> & { columns: IColumnAlias[], summary: IRemittanceItemSummary[] };
}
interface IRemittanceItemWithIncompleteDeductionsQueryResponse {
  remittanceItemsWithIncompleteDeductions: IQueryResponse<RemittanceItemProps> & { columns: IColumnAlias[] };
}

export class RemittanceRepository implements IRemittanceRepository {
  constructor(private readonly client: ApolloClient) {}

  // Get Remittances
  async getRemittances(params: IGetRemittanceParams, refetch: boolean = false): Promise<IRemittanceRepositoryResponse> {
    if (refetch) {
      this.invalidateAllRemittanceCache();
    }

    const { data } = await this.client.query<IRemittanceQueryResponse>({
      query: GET_REMITTANCES,
      variables: {
        params: {
          ...params,
          time_zone: TIME_ZONE,
        },
      },
    });

    if (!data?.remittances) {
      throw new Error("Failed to get remittances");
    }

    const remittancesResponse = data.remittances;

    return {
      remittances: remittancesResponse?.items.map((remittance: RemittanceProps) => new Remittance(remittance)),
      columns: remittancesResponse?.columns,
      totalCount: remittancesResponse?.total,
      currentPage: remittancesResponse?.page,
      pageLimit: remittancesResponse?.limit,
    };
  }

  // Get Remittances with incomplete deductions
  async getRemittanceWithIncompleteDeductions(
    params: IGetRemittanceWithIncompleteDeductionsParams,
    refetch: boolean = false
  ): Promise<IRemittanceRepositoryResponse> {
    if (refetch) {
      this.invalidateAllRemittanceCache();
    }

    const { data } = await this.client.query<IRemittanceWithIncompleteDeductionsQueryResponse>({
      query: GET_REMITTANCES_WITH_INCOMPLETE_DEDUCTIONS,
      variables: {
        params: {
          ...params,
          time_zone: TIME_ZONE,
        },
      },
    });

    if (!data?.remittancesWithIncompleteDeductions) {
      throw new Error("Failed to get remittances with incomplete deductions");
    }

    const remittancesResponse = data.remittancesWithIncompleteDeductions;

    return {
      remittances: remittancesResponse?.items.map((remittance: RemittanceProps) => new Remittance(remittance)),
      columns: remittancesResponse?.columns,
      totalCount: remittancesResponse?.total,
      currentPage: remittancesResponse?.page,
      pageLimit: remittancesResponse?.limit,
    };
  }

  // Invalidate Remittance cache
  private invalidateRemittanceCache(cache: ApolloCache) {
    const remittancesKey = "remittances";

    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    const rootQueryKeys = Object.keys(rawCache.ROOT_QUERY ?? {});
    for (const key of rootQueryKeys) {
      const remittancesCacheKeyMatch = new RegExp(`^${remittancesKey}((.*))$`).exec(key);

      if (remittancesCacheKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: remittancesKey,
        });
      }
    }
  }

  // Invalidate Remittance With Incomplete Deductions cache
  private invalidateRemittanceWithIncompleteDeductionsCache(cache: ApolloCache) {
    const remittancesWithIncompleteDeductionsKey = "remittancesWithIncompleteDeductions";

    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    const rootQueryWithIncompleteKeys = Object.keys(rawCache.ROOT_QUERY ?? {});
    for (const key of rootQueryWithIncompleteKeys) {
      const remittancesCacheWithIncompleteDeductionsKeyMatch = new RegExp(`^${remittancesWithIncompleteDeductionsKey}((.*))$`).exec(key);

      if (remittancesCacheWithIncompleteDeductionsKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: remittancesWithIncompleteDeductionsKey,
        });
      }
    }
  }

  private invalidateAllRemittanceCache() {
    this.invalidateRemittanceCache(this.client.cache);
    this.invalidateRemittanceWithIncompleteDeductionsCache(this.client.cache);
    this.invalidateRemittanceItemsCache(this.client.cache);
    this.invalidateRemittanceItemsWithIncompleteDeductionsCache(this.client.cache);
    this.invalidateRemittanceItemByIdCache(this.client.cache);
  }

  // Get Remittance Items
  async getRemittanceItems(params: IGetRemittanceParams, refetch: boolean = false): Promise<IRemittanceItemRepositoryResponse> {
    if (refetch) {
      this.invalidateAllRemittanceCache();
    }

    const { data } = await this.client.query<IRemittanceItemQueryResponse>({
      query: GET_REMITTANCE_ITEM,
      variables: {
        params: {
          ...params,
          time_zone: TIME_ZONE,
        },
      },
    });

    if (!data?.remittanceItems) {
      throw new Error("Failed to get remittance items");
    }

    const remittanceItemsResponse = data.remittanceItems;

    return {
      remittanceItems: remittanceItemsResponse?.items.map((remittanceItem: RemittanceItemProps) => new RemittanceItem(remittanceItem)),
      columns: remittanceItemsResponse?.columns,
      summary: remittanceItemsResponse?.summary,
      totalCount: remittanceItemsResponse?.total,
      currentPage: remittanceItemsResponse?.page,
      pageLimit: remittanceItemsResponse?.limit,
    };
  }

  // Get Remittance Items With Incomplete Deductions
  async getRemittanceItemWithIncompleteDeductions(
    params: IGetRemittanceWithIncompleteDeductionsParams,
    refetch: boolean = false
  ): Promise<Omit<IRemittanceItemRepositoryResponse, "summary">> {
    if (refetch) {
      this.invalidateAllRemittanceCache();
    }

    const { data } = await this.client.query<IRemittanceItemWithIncompleteDeductionsQueryResponse>({
      query: GET_REMITTANCE_ITEM_WITH_INCOMPLETE_DEDUCTIONS,
      variables: {
        params: {
          ...params,
          time_zone: TIME_ZONE,
        },
      },
    });

    if (!data?.remittanceItemsWithIncompleteDeductions) {
      throw new Error("Failed to get remittance items with incomplete deductions");
    }

    const remittanceItemsResponse = data.remittanceItemsWithIncompleteDeductions;

    return {
      remittanceItems: remittanceItemsResponse?.items.map((remittanceItem: RemittanceItemProps) => new RemittanceItem(remittanceItem)),
      columns: remittanceItemsResponse?.columns,
      totalCount: remittanceItemsResponse?.total,
      currentPage: remittanceItemsResponse?.page,
      pageLimit: remittanceItemsResponse?.limit,
    };
  }

  // Invalidate Remittance Items cache
  private invalidateRemittanceItemsCache(cache: ApolloCache) {
    const remittanceItemsKey = "remittanceItems";
    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    for (const key of Object.keys(rawCache.ROOT_QUERY ?? {})) {
      const remittanceItemsCacheKeyMatch = new RegExp(`^${remittanceItemsKey}((.*))$`).exec(key);

      if (remittanceItemsCacheKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: remittanceItemsKey,
        });
      }
    }
  }

  // Invalidate Remittance Items With Incomplete Deductions cache
  private invalidateRemittanceItemsWithIncompleteDeductionsCache(cache: ApolloCache) {
    const remittanceItemsWithIncompleteDeductionsKey = "remittanceItemsWithIncompleteDeductions";
    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    for (const key of Object.keys(rawCache.ROOT_QUERY ?? {})) {
      const remittanceItemsWithIncompleteDeductionsCacheKeyMatch = new RegExp(`^${remittanceItemsWithIncompleteDeductionsKey}((.*))$`).exec(key);

      if (remittanceItemsWithIncompleteDeductionsCacheKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: remittanceItemsWithIncompleteDeductionsKey,
        });
      }
    }
  }

  // Add Remittance
  async addRemittance(remittanceData: AddRemittanceDTO): Promise<IAddRemittanceProcessedResult> {
    const { data, error } = await this.client.mutate<IAddRemittanceProcessedResult>({
      mutation: ADD_REMITTANCE,
      variables: { params: remittanceData },
    });

    if (!data || error) {
      throw new Error("Failed to add remittance");
    }

    return data;
  }

  // Get Remittance Items count
  async getRemittanceItemsCount(params: IGetRemittanceItemCountParams, refetch: boolean = false): Promise<IRemittanceItemCountRepositoryResponse> {
    if (refetch) {
      this.invalidateRemittanceItemsCountCache(this.client.cache);
    }

    const { data, error } = await this.client.query<IRemittanceItemCountRepositoryResponse>({
      query: GET_REMITTANCE_ITEM_COUNT,
      variables: {
        params: {
          ...params,
        },
      },
    });

    if (!data || error) {
      throw new Error("Failed to get remittance items count");
    }

    return {
      ...data,
      remittanceItemCount: new DeductionCount(data.remittanceItemCount),
    };
  }

  // Invalidate Remittance Items count cache
  private invalidateRemittanceItemsCountCache(cache: ApolloCache) {
    const remittanceItemsCountKey = "remittanceItemCount";
    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    for (const key of Object.keys(rawCache.ROOT_QUERY ?? {})) {
      const remittanceItemsCacheKeyMatch = new RegExp(`^${remittanceItemsCountKey}((.*))$`).exec(key);

      if (remittanceItemsCacheKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: remittanceItemsCountKey,
        });
      }
    }
  }

  // Get Incomplete Deduction Resolution Status
  async getIncompleteDeductionResolutionStatus(
    params: IGetIncompleteDeductionResolutionStatusParams,
    refetch: boolean = true
  ): Promise<IIncompleteDeductionResolutionStatusRepositoryResponse> {
    if (refetch) {
      this.invalidateDeductionResolutionStatusCache(this.client.cache);
    }

    const { data } = await this.client.query<IIncompleteDeductionResolutionStatusRepositoryResponse>({
      query: GET_INCOMPLETE_DEDUCTION_RESOLUTION_STATUS,
      variables: {
        params: {
          ...params,
        },
      },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data?.deductionResolutionStatus) {
      throw new Error("Failed to get incomplete deduction resolution status");
    }

    // Convert the raw GraphQL response to the proper entity
    return {
      deductionResolutionStatus: new IncompleteDeductionResolutionStatus({
        remittanceItemId: data.deductionResolutionStatus.remittanceItemId,
        documents: data.deductionResolutionStatus.documents,
        purchaserKey: data.deductionResolutionStatus.purchaserKey,
      }),
    };
  }

  // Invalidate Deduction Resolution Status cache
  private invalidateDeductionResolutionStatusCache(cache: ApolloCache) {
    const deductionResolutionStatusKey = "deductionResolutionStatus";
    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    for (const key of Object.keys(rawCache.ROOT_QUERY ?? {})) {
      const deductionResolutionStatusCacheKeyMatch = new RegExp(`^${deductionResolutionStatusKey}((.*))$`).exec(key);

      if (deductionResolutionStatusCacheKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: deductionResolutionStatusKey,
        });
      }
    }
  }

  // Delete Deduction Backup Document
  async deleteDeductionBackupDocument(params: IDeleteDeductionBackupDocumentParams): Promise<IDeleteDeductionBackupDocumentRepositoryResponse> {
    const { data } = await this.client.mutate<IDeleteDeductionBackupDocumentRepositoryResponse>({
      mutation: DELETE_DEDUCTION_BACKUP_DOCUMENT,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to delete deduction backup document");
    }

    return data;
  }

  async getRemittanceItemById(params: IGetRemittanceItemByIdParams, refetch?: boolean): Promise<IGetRemittanceItemByIdRepositoryResponse> {
    if (refetch) {
      this.invalidateRemittanceItemByIdCache(this.client.cache);
    }

    const { data } = await this.client.query<IGetRemittanceItemByIdRepositoryResponse>({
      query: GET_REMITTANCE_ITEM_BY_ID,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to get remittance item by id");
    }

    return {
      remittanceItemById: data.remittanceItemById,
    };
  }

  // Invalidate Remittance Item By Id cache
  private invalidateRemittanceItemByIdCache(cache: ApolloCache) {
    const remittanceItemByIdKey = "remittanceItemById";
    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    for (const key of Object.keys(rawCache.ROOT_QUERY ?? {})) {
      const remittanceItemByIdCacheKeyMatch = new RegExp(`^${remittanceItemByIdKey}((.*))$`).exec(key);

      if (remittanceItemByIdCacheKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: remittanceItemByIdKey,
        });
      }
    }
  }

  // Download Remittance Documents
  async downloadRemittanceDocuments(params: IDownloadRemittanceDocumentsParams, formats: { date?: string; number?: string }): Promise<IDownloadRemittanceDocumentsRepositoryResponse> {
    return await new PrivateHttp({
      headers: {
        "Content-Type": ContentType.JSON,
      },
    }).post<IDownloadRemittanceDocumentsRepositoryResponse>(ApiEndpoints.EXPORT_REMITTANCE_DOCUMENTS, {
      params: params,
      formats: formats,
    });
  }
}
